import { Divider, Typography, Link } from '@mui/material';
import { Box } from '@mui/system';
import { useCallback, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { push } from '../../../../app/reducers';
import { ACCOUNT_LINKING_TERMS_DOC } from '../../../../domain/config';
import { SuccessChip } from '../../../../general/components/success-chip';
import { VERIFY_REVENUE_MX } from '../../../../paths';
import { RootState } from '../../../../store';
import { recordConsent } from '../../../legal-consent/reducers/consent';
import { accountLinkingTermsSelector } from '../../../legal-consent/selectors/legal-document-selectors';
import { BankIconCircled } from '../../icons';
import { ChoiceContainer } from '../choice-container/choice-container';

const selector = ({ bankAccountLinking: { revenueVerificationStatus } }: RootState) => ({
  revenueVerificationStatus,
});

const MxVerifyRevenueTile: FC = () => {
  const dispatch = useDispatch();
  const handleMxChoice = useCallback(() => {
    dispatch(
      recordConsent({
        documentKeys: [ACCOUNT_LINKING_TERMS_DOC],
        onSuccess: [push(VERIFY_REVENUE_MX)],
      }),
    );
  }, [dispatch]);

  const { revenueVerificationStatus } = useSelector(selector);
  const accountLinkingTermsAgreement = useSelector(accountLinkingTermsSelector);
  const success = !revenueVerificationStatus || revenueVerificationStatus === 'success';

  const linkAccountTitle = success ? (
    <>
      Link your business bank
      <br /> account
    </>
  ) : (
    <>
      Link a different business bank <br />
      account
    </>
  );

  const linkAccountSubTitle = success ? (
    <>
      Securely connect to your financial institution and select an account.
      <br /> We do not store your credentials when you give us access to your account.
    </>
  ) : (
    ''
  );

  return (
    <ChoiceContainer
      icon={<BankIconCircled />}
      title={linkAccountTitle}
      subtitle={linkAccountSubTitle}
      onChoice={handleMxChoice}
    >
      <Box>
        <SuccessChip size="small" label="Recommended" />
      </Box>
      <Divider />
      <Typography variant="caption" color="textSecondary" component="div">
        By linking a bank account, you agree to Coast’s{' '}
        {accountLinkingTermsAgreement ? (
          <Link href={accountLinkingTermsAgreement.url} target="_blank">
            {accountLinkingTermsAgreement.name}
          </Link>
        ) : (
          'Account Linking Terms'
        )}
        . These Terms describe how we access your financial data to administer and service your Coast account.
      </Typography>
    </ChoiceContainer>
  );
};

export { MxVerifyRevenueTile };
